import Header from "@/components/bionexus/Header";
import Footer from "@/components/Footer";
import BackNav from "@/components/BackNav";
import { useParams } from "react-router-dom";
import { Link } from "@/components/LocaleLink";
import { useI18n } from "@/i18n/I18nProvider";
import { applicationTypeLabel, italianName, localizedName, translatedText } from "@/i18n/content";
import SeoAlternates from "@/components/SeoAlternates";
import { materialJsonLd } from "@/lib/structuredData";
import { useMaterial, useMaterialApplications, useMaterials } from "@/hooks/useBioNexusData";
import { FileText, ExternalLink, CheckCircle, BookOpen, Beaker, Zap, Droplets, DollarSign, Clock, FlaskConical } from "lucide-react";
import { Helmet } from "react-helmet-async";
import Reveal from "@/components/Reveal";
import MaterialCard, { ImageBadge } from "@/components/cards/MaterialCard";
import DetailHero from "@/components/detail/DetailHero";
import { DetailSection, Eyebrow, OnThisPage, ReadingProgress, SectionLink } from "@/components/detail/DetailLayout";
import { ExpandableText, KeyFacts, SpotlightCard, TRLMeter } from "@/components/detail/DataDisplay";
import { splitLead } from "@/lib/text";

const resolveMaterialImageUrl = (url?: string | null) => {
  if (!url) return null;
  // Local public assets
  if (url.startsWith("/")) return url;

  // Some academic publishers block hotlinking; route through our backend proxy for reliability.
  const lower = url.toLowerCase();
  if (lower.includes("ncbi.nlm.nih.gov")) {
    const base = import.meta.env.VITE_SUPABASE_URL;
    return `${base}/functions/v1/imageproxy?url=${encodeURIComponent(url)}`;
  }

  return url;
};


const SourceNote = ({ source, className = "" }: { source: string; className?: string }) => {
  const { m } = useI18n();
  return (
    <p className={`flex items-start gap-1.5 text-xs text-muted-foreground ${className}`}>
      <BookOpen className="w-3 h-3 mt-0.5 flex-shrink-0" />
      <span>
        {m.common.source}: <span lang="en">{source}</span>
      </span>
    </p>
  );
};

// Component for displaying a measured data point with its source
const MeasuredData = ({
  icon: Icon,
  label,
  value,
  source,
  unit,
  method
}: {
  icon: React.ElementType;
  label: string;
  value: string | number | null | undefined;
  source?: string | null;
  unit?: string;
  method?: string | null;
}) => {
  const { m } = useI18n();
  if (!value) return null;

  return (
    <SpotlightCard className="flex flex-col">
      <div className="flex items-center gap-2 mb-4">
        <Icon className="w-4 h-4 text-primary" />
        <Eyebrow>{label}</Eyebrow>
      </div>
      <p lang="en" className="text-[1.8rem] font-medium leading-snug text-foreground">
        {value}
        {unit && <span className="text-sm font-normal text-muted-foreground ml-1">{unit}</span>}
      </p>
      {(source || method) && (
        <div className="mt-auto pt-5">
          <div className="border-t border-border/60 pt-4 space-y-1.5">
            {method && <p className="text-xs text-muted-foreground">{m.common.method}: <span lang="en">{method}</span></p>}
            {source && (
              <p className="flex items-start gap-1.5 text-xs text-muted-foreground/80" title={source}>
                <BookOpen className="w-3 h-3 mt-0.5 flex-shrink-0" />
                <span lang="en" className="line-clamp-2">{source}</span>
              </p>
            )}
          </div>
        </div>
      )}
    </SpotlightCard>
  );
};

const MaterialDetail = () => {
  const { id } = useParams();
  const { data: material, isLoading } = useMaterial(id);
  const { data: relatedApps } = useMaterialApplications(id);
  const { data: allMaterials } = useMaterials();

  const { m, locale, formatDate } = useI18n();
  const relatedMaterials = allMaterials?.filter((x) => x.id !== id && x.family === material?.family).slice(0, 4) || [];
  const name = localizedName(locale, material?.name || "", material?.name_cn, italianName(material, "name"));
  const desc = translatedText(material, "description", material?.description, locale);
  const fabrication = translatedText(material, "fabrication_method", material?.fabrication_method, locale);
  const primaryUse = translatedText(material, "primary_application", material?.primary_application, locale);
  const relName = (a?: { title?: string | null; title_cn?: string | null } | null) => localizedName(locale, a?.title || "", a?.title_cn);

  // Count available properties
  const availableProperties = [
    material?.degradation_value || material?.degradation_days,
    material?.tensile_strength_mpa,
    material?.electrical_performance,
    material?.water_resistance,
    material?.cost_per_kg,
    material?.trl_level
  ].filter(Boolean).length;

  const degradation = material?.degradation_value || (material?.degradation_days ? `${material.degradation_days} days` : null);
  const { lead, rest } = splitLead(desc.text || "");
  const hasContext = !!(material?.comparison_materials?.length || material?.paper_figures?.length);

  const S = m.material.sections;
  const sections: SectionLink[] = [
    material?.description && { id: "overview", title: S.overview },
    availableProperties > 0 && { id: "properties", title: S.properties },
    material?.paper_abstract && { id: "abstract", title: S.abstract },
    material?.fabrication_method && { id: "fabrication", title: S.fabrication },
    hasContext && { id: "context", title: S.context },
    relatedApps && relatedApps.length > 0 && { id: "applications", title: S.applications },
  ].filter(Boolean) as SectionLink[];

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="pt-20">
          <div className="h-[60vh] bg-muted animate-pulse" />
          <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-16">
            <div className="h-12 bg-muted w-1/2 mb-4 animate-pulse" />
            <div className="h-6 bg-muted w-3/4 animate-pulse" />
          </div>
        </main>
      </div>
    );
  }

  if (!material) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="pt-32 text-center px-6">
          <h1 className="font-display font-medium text-3xl mb-4">{m.material.notFound}</h1>
          <Link to="/materials" className="text-primary hover:underline">
            ← {m.common.backMaterials}
          </Link>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background page-enter">
      <Helmet>
        <title>{`${name.primary} — BioNexus`}</title>
        <meta name="description" content={m.meta.materialDescription(name.primary, material.family)} />
        <meta property="og:title" content={`${name.primary} — BioNexus`} />
        <meta property="og:description" content={m.meta.materialDescription(name.primary, material.family)} />
        <meta property="og:type" content="article" />
        {material.image_url && <meta property="og:image" content={material.image_url.startsWith("http") ? material.image_url : `https://bionexus.news${material.image_url}`} />}
        <script type="application/ld+json">
          {JSON.stringify(materialJsonLd(material, locale, { name: name.primary, description: desc.text }))}
        </script>
      </Helmet>
      <SeoAlternates path={`/materials/${material.id}`} />
      <Header />
      <ReadingProgress />

      <main className="pt-20">
        <DetailHero
          image={resolveMaterialImageUrl(material.image_url)}
          imageAlt={name.primary}
          credit={material.image_credit}
          creditUrl={material.image_source_url || material.source_url}
          eyebrow={<ImageBadge material={material} />}
          title={name.primary}
          titleLang={name.primaryLang}
          subtitle={
            <>
              <span lang="en" className="text-primary">{material.family}</span>
              {name.secondary && <span lang={name.secondaryLang} className="ml-3">{name.secondary}</span>}
            </>
          }
        />

        <KeyFacts
          facts={[
            { label: m.material.facts.family, value: material.family && <span lang="en">{material.family}</span> },
            { label: m.material.facts.primaryUse, value: primaryUse.text && <span lang={primaryUse.lang}>{primaryUse.text}</span> },
            { label: m.material.facts.degradation, value: degradation && <span lang="en">{degradation}</span> },
            { label: m.material.facts.trl, node: material.trl_level ? <TRLMeter level={material.trl_level} compact /> : null },
          ]}
        />

        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 pt-12 md:pt-16">
          <div className="mb-12 flex flex-wrap items-baseline justify-between gap-4">
            <BackNav to="/materials" label={m.common.backMaterials} />
            {m.common.contentLanguageNote && !desc.translated && <p className="text-xs text-muted-foreground">{m.common.contentLanguageNote}</p>}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 lg:gap-12 xl:gap-20">
            {/* Article */}
            <article className="lg:col-span-8 space-y-20 md:space-y-24 min-w-0">
              {material.description && (
                <DetailSection id="overview" title={S.overview}>
                  <div className="max-w-[68ch]">
                    <p lang={desc.lang} className="font-display font-normal text-[1.8rem] md:text-[2rem] leading-[1.45] text-balance">
                      {lead}
                    </p>
                    {rest && <p lang={desc.lang} className="mt-6 text-muted-foreground leading-[1.8]">{rest}</p>}
                  </div>
                </DetailSection>
              )}

              {availableProperties > 0 && (
                <DetailSection id="properties" title={S.properties}>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <MeasuredData
                      icon={Clock}
                      label={m.material.props.degradation}
                      value={degradation}
                      source={material.degradation_source}
                      method={material.degradation_method}
                    />
                    <MeasuredData
                      icon={Beaker}
                      label={m.material.props.tensile}
                      value={material.tensile_strength_mpa}
                      unit="MPa"
                      source={material.tensile_source}
                    />
                    <MeasuredData
                      icon={Zap}
                      label={m.material.props.electrical}
                      value={material.electrical_performance}
                      source={material.electrical_source}
                    />
                    <MeasuredData
                      icon={Droplets}
                      label={m.material.props.water}
                      value={material.water_resistance}
                      source={material.water_resistance_source}
                    />
                    <MeasuredData
                      icon={DollarSign}
                      label={m.material.props.cost}
                      value={material.cost_per_kg ? `$${material.cost_per_kg}` : null}
                      unit="/kg"
                      source={material.cost_source}
                    />
                    {material.trl_level && (
                      <SpotlightCard>
                        <div className="flex items-center gap-2 mb-4">
                          <FlaskConical className="w-4 h-4 text-primary" />
                          <Eyebrow>{m.material.props.trl}</Eyebrow>
                        </div>
                        <TRLMeter level={material.trl_level} />
                      </SpotlightCard>
                    )}
                  </div>
                </DetailSection>
              )}

              {material.paper_abstract && (
                <DetailSection id="abstract" title={S.abstract}>
                  <figure className="relative max-w-[68ch] pl-8 md:pl-10">
                    <span aria-hidden className="absolute left-0 -top-4 font-display text-6xl leading-none text-primary/40 select-none">
                      &ldquo;
                    </span>
                    <ExpandableText text={material.paper_abstract} className="text-muted-foreground italic" />
                    {material.source_paper && (
                      <figcaption lang="en" className="mt-6 font-mono text-xs text-muted-foreground/80">— {material.source_paper}</figcaption>
                    )}
                  </figure>
                </DetailSection>
              )}

              {material.fabrication_method && (
                <DetailSection id="fabrication" title={S.fabrication}>
                  <div className="max-w-[68ch]">
                    <p lang={fabrication.lang} className="leading-[1.8]">{fabrication.text}</p>
                    {material.fabrication_source && <SourceNote source={material.fabrication_source} className="mt-5" />}
                  </div>
                </DetailSection>
              )}

              {hasContext && (
                <DetailSection id="context" title={S.context}>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                    {material.comparison_materials && material.comparison_materials.length > 0 && (
                      <div>
                        <Eyebrow className="mb-4">{m.material.comparedWith}</Eyebrow>
                        <div className="flex flex-wrap gap-2">
                          {material.comparison_materials.map((mat: string, i: number) => (
                            <span key={i} className="font-mono text-xs rounded-full border border-border px-3 py-1.5 transition-colors hover:border-primary/60">
                              <span lang="en">{mat}</span>
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    {material.paper_figures && material.paper_figures.length > 0 && (
                      <div>
                        <Eyebrow className="mb-4">{m.material.referencedFigures}</Eyebrow>
                        <div className="flex flex-wrap gap-2">
                          {material.paper_figures.map((fig: string, i: number) => (
                            <span key={i} className="font-mono text-xs rounded-full bg-primary/10 text-primary px-3 py-1.5">
                              <span lang="en">{fig}</span>
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </DetailSection>
              )}

              {relatedApps && relatedApps.length > 0 && (
                <DetailSection id="applications" title={S.applications}>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    {relatedApps.map((item: any) => (
                      <Link key={item.application_id} to={`/applications/${item.application_id}`} className="group block">
                        <div className="aspect-video bg-muted mb-4 overflow-hidden rounded-lg">
                          {item.applications?.image_url ? (
                            <img
                              src={resolveMaterialImageUrl(item.applications.image_url) ?? undefined}
                              alt={item.applications?.title}
                              loading="lazy"
                              className="w-full h-full object-cover transition-transform duration-700 ease-expo-out group-hover:scale-105"
                            />
                          ) : (
                            <div className="w-full h-full bg-gradient-to-br from-primary/15 to-muted" />
                          )}
                        </div>
                        <span className="font-mono text-xs uppercase text-primary">
                          {item.applications?.application_type ? applicationTypeLabel(m, item.applications.application_type) : m.material.application}
                        </span>
                        <h3 className="font-display font-medium text-lg mt-1 leading-snug group-hover:text-primary transition-colors">
                          <span lang={relName(item.applications).primaryLang}>{relName(item.applications).primary || m.material.application}</span>{" "}
                          <span className="arrow-nudge">→</span>
                        </h3>
                      </Link>
                    ))}
                  </div>
                </DetailSection>
              )}
            </article>

            {/* Sidebar */}
            <aside className="lg:col-span-4 lg:sticky lg:top-32 lg:self-start space-y-10">
              <OnThisPage sections={sections} />

              {(material.source_paper || material.source_doi || material.source_url) && (
                <div className="rounded-lg border border-primary/30 bg-gradient-to-br from-primary/[0.08] to-transparent p-6">
                  <Eyebrow className="text-primary flex items-center gap-2 mb-4">
                    <FileText className="w-3.5 h-3.5" />
                    {m.material.researchSource}
                  </Eyebrow>
                  {material.source_paper && (
                    <p lang="en" className="font-display font-medium text-lg leading-snug mb-3">{material.source_paper}</p>
                  )}
                  {material.source_doi && (
                    <p className="font-mono text-xs text-muted-foreground mb-5 break-all">DOI: {material.source_doi}</p>
                  )}
                  <div className="flex flex-wrap gap-2">
                    {material.source_url && (
                      <a
                        href={material.source_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="group inline-flex items-center gap-2 rounded-full bg-primary px-4 py-2 font-mono text-xs text-primary-foreground transition-transform hover:-translate-y-0.5"
                      >
                        {m.material.viewPaper} <ExternalLink className="w-3 h-3" />
                      </a>
                    )}
                    {material.source_doi && (
                      <a
                        href={`https://doi.org/${material.source_doi}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 rounded-full border border-border px-4 py-2 font-mono text-xs transition-colors hover:border-primary hover:text-primary"
                      >
                        DOI <ExternalLink className="w-3 h-3" />
                      </a>
                    )}
                  </div>
                </div>
              )}

              {material.applications_mentioned && material.applications_mentioned.length > 0 && (
                <div>
                  <Eyebrow className="mb-4">{m.material.applicationsFromPaper}</Eyebrow>
                  <ul className="space-y-2.5">
                    {material.applications_mentioned.map((app: string, i: number) => (
                      <li key={i} className="text-sm flex items-start gap-3">
                        <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
                        <span lang="en">{app}</span>
                      </li>
                    ))}
                  </ul>
                  {material.applications_source && <SourceNote source={material.applications_source} className="mt-4" />}
                </div>
              )}

              <p className="font-mono text-xs text-muted-foreground/70">{m.common.lastUpdated(formatDate(material.updated_at))}</p>
            </aside>
          </div>

          {/* Related Materials in Same Family */}
          {relatedMaterials.length > 0 && (
            <section className="mt-28 pt-12 border-t border-border">
              <div className="flex justify-between items-baseline mb-10">
                <h2 className="font-display font-medium text-2xl md:text-3xl tracking-tight">
                  {m.material.moreFromFamily(material.family || "")}
                </h2>
                <Link
                  to={`/materials?family=${encodeURIComponent(material.family || "")}`}
                  className="group text-sm font-medium text-primary"
                >
                  {m.common.viewAll} <span className="arrow-nudge">→</span>
                </Link>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {relatedMaterials.map((mat, i) => (
                  <Reveal key={mat.id} delay={i * 80} className="h-full">
                    <MaterialCard material={mat} resolveImage={resolveMaterialImageUrl} />
                  </Reveal>
                ))}
              </div>
            </section>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default MaterialDetail;
